import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { useSummaryTemplates } from "../../hooks/useSummaryTemplates";
import type { SummaryTemplate } from "../../types/summaryTemplate/SummaryTemplate";
const isSystemTemplate = (t: SummaryTemplate) => t.customerId === "system";
const isPersonalTemplate = (t: SummaryTemplate) =>
  t.customerId !== "system" && t.userId != null;
const isCustomerTemplate = (t: SummaryTemplate) =>
  t.customerId !== "system" && t.userId == null;

interface TemplateGroupProps {
  title: string;
  hint: string;
  borderColor: string;
  templates: SummaryTemplate[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
}

function TemplateGroup({
  title,
  hint,
  borderColor,
  templates,
  selectedId,
  onSelect,
}: TemplateGroupProps) {
  const { t } = useTranslation("summaryTemplate");

  if (templates.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.5 }}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          sx={{ color: borderColor }}
        >
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      </Box>
      <List dense disablePadding>
        {templates.map((tpl) => {
          const isSelected = selectedId === tpl.id;
          const sectionNames = tpl.sections
            .sort((a, b) => a.order - b.order)
            .map((s) => s.heading)
            .join(", ");

          return (
            <ListItemButton
              key={tpl.id}
              selected={isSelected}
              onClick={() => onSelect(tpl.id)}
              sx={{
                mb: 0.5,
                borderRadius: 1,
                border: "2px solid",
                borderColor: isSelected ? borderColor : "transparent",
                bgcolor: isSelected ? `${borderColor}10` : "background.paper",
                "&:hover": {
                  borderColor: borderColor,
                  bgcolor: `${borderColor}08`,
                },
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={isSelected ? 600 : 400}
                    >
                      {tpl.name}
                    </Typography>
                    {tpl.isDefault && (
                      <Typography
                        variant="caption"
                        sx={{
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          px: 0.75,
                          py: 0.1,
                          borderRadius: 0.5,
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          lineHeight: 1.6,
                        }}
                      >
                        {t("default")}
                      </Typography>
                    )}
                  </Box>
                }
                secondary={
                  sectionNames
                    ? sectionNames
                    : `${tpl.outputFormat} · ${tpl.lengthGuidance}`
                }
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}

export interface TemplatePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (templateId: string) => void;
  customerId: string;
}

export function TemplatePickerDialog({
  open,
  onClose,
  onSelect,
  customerId,
}: TemplatePickerDialogProps) {
  const { t } = useTranslation(["summaryTemplate", "common"]);
  const { user } = useContext(AuthContext);
  const { data, isLoading, error } = useSummaryTemplates(
    open ? customerId : undefined,
  );
  const templates = data?.templates ?? [];
  const hiddenSystemTemplateIds = data?.hiddenSystemTemplateIds ?? [];
  const systemTemplates = templates.filter(
    (t) => isSystemTemplate(t) && !hiddenSystemTemplateIds.includes(t.id),
  );
  const customerTemplates = templates.filter(isCustomerTemplate);
  const personalTemplates = templates.filter(isPersonalTemplate);

  const defaultTemplate = templates.find((tpl) => tpl.isDefault);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  // Pre-select the default template whenever the dialog opens or data loads
  useEffect(() => {
    if (open && defaultTemplate && !selectedId) {
      setSelectedId(defaultTemplate.id);
    }
  }, [open, defaultTemplate, selectedId]);

  // Reset selection when the dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedId(undefined);
    }
  }, [open]);

  const handleGenerate = () => {
    if (selectedId) {
      onSelect(selectedId);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="template-picker-title"
    >
      <DialogTitle id="template-picker-title">
        <Typography variant="h6">
          {t("summaryTemplate:selectTemplate")}
        </Typography>
        <IconButton
          aria-label={t("common:close")}
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error">
            {error.message}
          </Typography>
        ) : templates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("summaryTemplate:selectTemplateHint")}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("summaryTemplate:selectTemplateHint")}
            </Typography>
            <TemplateGroup
              title={t("summaryTemplate:systemTemplates")}
              hint={t("summaryTemplate:systemTemplatesHint")}
              borderColor="#1976d2"
              templates={systemTemplates}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
            <TemplateGroup
              title={t("summaryTemplate:customerTemplates")}
              hint={t("summaryTemplate:customerTemplatesHint")}
              borderColor="#388e3c"
              templates={customerTemplates}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
            <TemplateGroup
              title={t("summaryTemplate:myTemplates")}
              hint={t("summaryTemplate:myTemplatesHint")}
              borderColor="#f57c00"
              templates={personalTemplates.filter(
                (tpl) => tpl.userId === user?.userId,
              )}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t("common:cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={!selectedId}
          onClick={handleGenerate}
        >
          {t("summaryTemplate:generateSummary")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
